import React, { useState } from 'react';
import AgencyNavbar from '../components/layout/AgencyNavbar';
import AgencyFooter from '../components/layout/AgencyFooter';
import { CheckCircle2, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useSiteContent } from '../contexts/SiteContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoPage = () => {
  const { getPageContent, settings } = useSiteContent();
  const content = getPageContent('demo') || {};
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    nombreNegocio: '',
    tipoOperacion: '',
    preferenciaContacto: '',
    pedidosMensuales: '',
    aceptaNovedades: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.email || !formData.telefono) {
      toast.error('Por favor completá los campos obligatorios');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/demo-request`, formData);
      setIsSubmitted(true);
      toast.success('¡Solicitud enviada con éxito!');
    } catch (error) {
      toast.error(content.error_title || 'Error al enviar la solicitud', {
        description: content.error_message || 'Intentá de nuevo más tarde.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const brandName = settings.brand_name || 'mispedidos app';

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black text-white">
        <AgencyNavbar />
        <div className="pt-24 pb-16 px-4 min-h-screen flex items-center justify-center">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-24 h-24 rounded-full bg-[#E9FF60] flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="w-12 h-12 text-black" />
            </div>
            <h1 className="text-4xl font-black mb-4 text-[#E9FF60]" data-testid="success-title">
              {content.success_title || '¡Solicitud Enviada!'}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {content.success_message || 'Gracias por tu interés. Nos pondremos en contacto con vos en las próximas 24 horas.'}
            </p>
            <a
              href="/"
              className="inline-block bg-[#E9FF60] hover:bg-[#f0ff80] text-black px-8 py-4 rounded-full font-bold"
            >
              Volver al Inicio
            </a>
          </div>
        </div>
        <AgencyFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <AgencyNavbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 
              className="text-5xl md:text-6xl font-black mb-4"
              style={{ fontFamily: 'Unbounded, Montserrat, sans-serif' }}
              data-testid="demo-title"
            >
              <span className="text-[#E9FF60]">{content.title_1 || 'SOLICITÁ'}</span>
              <br />
              <span className="text-white">{content.title_2 || 'UNA DEMO'}</span>
            </h1>
            <p className="text-xl text-gray-300">
              {content.subtitle || `Completá el formulario y nos contactamos para mostrarte cómo funciona tu nueva plataforma.`}
            </p>
          </div>

          {/* Form */}
          <div className="bg-[#1C1C1C] border border-[#333] rounded-2xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nombre y Apellido */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Nombre *</label>
                  <Input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="bg-black border-[#333] text-white"
                    placeholder="Tu nombre"
                    data-testid="demo-nombre"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Apellido *</label>
                  <Input
                    type="text"
                    name="apellido"
                    value={formData.apellido}
                    onChange={handleChange}
                    required
                    className="bg-black border-[#333] text-white"
                    placeholder="Tu apellido"
                    data-testid="demo-apellido"
                  />
                </div>
              </div>

              {/* Email y Teléfono */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-black border-[#333] text-white"
                    placeholder="tu@email.com"
                    data-testid="demo-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Teléfono *</label>
                  <Input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="bg-black border-[#333] text-white"
                    placeholder="+54 342 123 4567"
                    data-testid="demo-telefono"
                  />
                </div>
              </div>

              {/* Nombre del Negocio */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Nombre de tu Negocio</label>
                <Input
                  type="text"
                  name="nombreNegocio"
                  value={formData.nombreNegocio}
                  onChange={handleChange}
                  className="bg-black border-[#333] text-white"
                  placeholder="Ej: Pizzería Don Juan"
                  data-testid="demo-negocio"
                />
              </div>

              {/* Tipo de Operación */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Tipo de Operación</label>
                <select
                  name="tipoOperacion"
                  value={formData.tipoOperacion}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black border border-[#333] rounded-md text-white"
                  data-testid="demo-tipo"
                >
                  <option value="">Seleccioná una opción</option>
                  <option value="restaurante">Restaurante</option>
                  <option value="delivery">Solo Delivery</option>
                  <option value="cafeteria">Cafetería</option>
                  <option value="panaderia">Panadería</option>
                  <option value="heladeria">Heladería</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              {/* Preferencia de Contacto */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">¿Cómo preferís que te contactemos?</label>
                <div className="flex flex-wrap gap-4">
                  {['WhatsApp', 'Email', 'Llamada'].map((option) => (
                    <label key={option} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="preferenciaContacto"
                        value={option.toLowerCase()}
                        checked={formData.preferenciaContacto === option.toLowerCase()}
                        onChange={handleChange}
                        className="w-4 h-4 accent-[#E9FF60]"
                      />
                      <span className="text-gray-300">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Pedidos Mensuales */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">¿Cuántos pedidos mensuales estimás?</label>
                <select
                  name="pedidosMensuales"
                  value={formData.pedidosMensuales}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black border border-[#333] rounded-md text-white"
                  data-testid="demo-pedidos"
                >
                  <option value="">Seleccioná una opción</option>
                  <option value="0-100">Menos de 100</option>
                  <option value="100-500">Entre 100 y 500</option>
                  <option value="500-1000">Entre 500 y 1000</option>
                  <option value="1000+">Más de 1000</option>
                </select>
              </div>

              {/* Newsletter Checkbox */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="aceptaNovedades"
                    checked={formData.aceptaNovedades}
                    onChange={handleChange}
                    className="w-5 h-5 mt-1 accent-[#E9FF60]"
                    data-testid="demo-newsletter"
                  />
                  <span className="text-sm text-gray-400">
                    Acepto recibir novedades y promociones de {brandName}
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#E9FF60] hover:bg-[#f0ff80] text-black py-6 text-lg font-bold"
                data-testid="demo-submit"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    Solicitar Demo Gratis
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <AgencyFooter />
    </div>
  );
};

export default DemoPage;
